import { useGetStuckReports, useRegenerateReport, useRequeueReportPdf } from '@actions';
import Button from '@components/V4/Button';
import Modal from '@components/V4/Modal/Modal';
import Table from '@components/V4/Table/Table';
import { reportApis } from '@shared/apis';
import { StuckReport } from '@shared/apis/report.apis';
import { ReportStatus } from '@shared/enums';
import useToast from 'apps/agora/src/hooks/useToast';
import moment from 'moment';
import { useQueryClient } from 'react-query';

type StuckReportsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const StuckReportRow = ({ report }: { report: StuckReport }) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries(reportApis.getStuckReports.endpoint);
    queryClient.invalidateQueries(reportApis.searchReports.endpoint);
  };

  const { mutate: regenerate, isLoading: isRegenerating } = useRegenerateReport(report._id, {
    onSuccess: () => {
      toast.success(`Rebuilt ${report.mentorName}'s report. The PDF is queued.`);
      invalidate();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message ?? 'The report could not be rebuilt.');
    },
  });

  const { mutate: requeuePdf, isLoading: isRequeueing } = useRequeueReportPdf(report._id, {
    onSuccess: () => {
      toast.success(`PDF re-queued for ${report.mentorName}.`);
      invalidate();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message ?? 'The PDF could not be re-queued.');
    },
  });

  const isError = report.status === ReportStatus.ERROR;

  return (
    <Table.Row>
      <Table.Cell>{report.mentorName}</Table.Cell>
      <Table.Cell>{`${report.month}/${report.year}`}</Table.Cell>
      <Table.Cell>
        {isError ? 'Error' : 'PDF stuck'}
        {isError && report.isEmpty && (
          <span className="block text-customGrey">no data — needs a rebuild</span>
        )}
      </Table.Cell>
      <Table.Cell>{moment(report.updatedAt).format('DD MMM YYYY, HH:mm')}</Table.Cell>
      <Table.Cell>
        <span className="block max-w-[260px] truncate text-customGrey" title={report.errorMessage}>
          {report.errorMessage ?? '—'}
        </span>
      </Table.Cell>
      <Table.Cell>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            className="h-6 min-h-6"
            isLoading={isRegenerating}
            buttonText="Rebuild"
            onClick={() => regenerate({})}
          />
          <Button
            variant="secondary"
            className="h-6 min-h-6"
            isLoading={isRequeueing}
            buttonText="Retry PDF"
            onClick={() => requeuePdf({})}
          />
        </div>
      </Table.Cell>
    </Table.Row>
  );
};

const StuckReportsModal = (props: StuckReportsModalProps) => {
  const { isOpen, onClose } = props;

  const { data, isLoading } = useGetStuckReports({ enabled: isOpen });

  const reports = data?.reports ?? [];

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header title="Reports that did not go through" />
      <Modal.Body className="flex flex-col gap-4 text-xs">
        <p className="text-customGrey">
          Reports that ended in an error, and reports whose PDF has been generating for more than 30
          minutes — across every month. <b>Rebuild</b> cancels the report and generates it again
          from the sessions; <b>Retry PDF</b> only puts the existing report back in the PDF queue.
        </p>

        {isLoading && <p>Loading...</p>}

        {!isLoading && !reports.length && <p>Nothing is stuck. Every report went through.</p>}

        {!!reports.length && (
          <Table size="small">
            <Table.Header>
              <Table.Row isHeader>
                <Table.HeaderCell>Mentor</Table.HeaderCell>
                <Table.HeaderCell>Month</Table.HeaderCell>
                <Table.HeaderCell>Problem</Table.HeaderCell>
                <Table.HeaderCell>Last change</Table.HeaderCell>
                <Table.HeaderCell>Error</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {reports.map((report) => (
                <StuckReportRow key={report._id} report={report} />
              ))}
            </Table.Body>
          </Table>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default StuckReportsModal;
